import { isNil } from 'lodash'
import React from 'react'
import styled from 'styled-components'
import { FireGradientSVG } from 'src/ui/svgs'
import { BasketsListItem } from '../../../api'
import * as dt from '../../../ui/design-tokens/tokens'
import { formatCurrency } from '../../../utils'

const formatBasketCurrency = (amount: number | undefined) => {
  if (amount === null || amount === undefined) {
    return '-'
  }
  const _1Mil = 1000000
  const _1Bil = _1Mil * 1000
  if (Math.abs(amount) > _1Bil) {
    return `$${Math.round((amount * 100) / _1Bil) / 100}B`
  }
  if (Math.abs(amount) > _1Mil) {
    return `$${Math.round((amount * 100) / _1Mil) / 100}M`
  }
  return formatCurrency(amount, 0)
}

const calculateLikelihood = (priceScoreLikelihoods: number[] = [], targetPercentile: number) => {
  // Percentile index is 1 less than percentile number
  const result = priceScoreLikelihoods[targetPercentile - 1]
    ? Math.round(priceScoreLikelihoods[targetPercentile - 1] * 100)
    : null
  if (result && result > 99) {
    return 99
  }
  return result
}

const getLikelihoodDisplay = (likelihood?: number | null): { color: string; label: string } => {
  if (isNil(likelihood)) {
    return { color: dt.ColorNeutral90, label: '-' }
  }
  if (likelihood >= 80) {
    return { color: dt.ColorGreen70, label: 'High' }
  }
  if (likelihood < 50) {
    return { color: dt.ColorRed50, label: 'Low' }
  }
  return { color: dt.ColorNeutral90, label: 'Medium' }
}

type Props = { basket: BasketsListItem; targetPercentile: number }

export const BasketListItemStatistics: React.FC<Props> = ({ basket, targetPercentile }) => {
  const likelihood = basket.likelihoods ? calculateLikelihood(basket.likelihoods, targetPercentile) : null
  const likelihoodDisplay = getLikelihoodDisplay(likelihood)
  return (
    <StatisticsWrapper>
      <Statistic>
        <div className="value" style={{ color: dt.ColorNeutral90 }}>
          {formatBasketCurrency(basket.opportunity)}
        </div>
        <div className="label">Opportunity</div>
      </Statistic>
      {basket.opportunity > 0 && (
        <Statistic>
          <div
            className="value"
            style={{
              color: likelihoodDisplay.color,
            }}>
            {likelihoodDisplay.label}
            {likelihoodDisplay.label === 'High' ? <FireGradientSVG /> : null}
          </div>
          <div className="label">Likelihood</div>
        </Statistic>
      )}
      <Statistic>
        <div className="value">{formatBasketCurrency(basket.totalSpend)}</div>
        <div className="label" title="Rolling 12M Spend">
          12M Spend
        </div>
      </Statistic>
    </StatisticsWrapper>
  )
}

const StatisticsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const Statistic = styled.div`
  &:last-child {
    text-align: right;
    .value {
      justify-content: flex-end;
    }
  }
  .label {
    color: ${dt.ColorNeutral70};
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.03rem;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .value {
    color: ${dt.ColorNeutral90};
    font-size: ${dt.TextDisplaySmallFontSize};
    font-weight: ${dt.TextDisplaySmallFontWeight};

    display: flex;
    align-items: center;
    gap: 2px;
  }
`
